import { useEffect, useReducer, useState, useRef } from "react";
import { nanoid } from "nanoid";
import { get, set } from "./storage";

const API_URL = "https://salidas-backend.fly.dev";
const API_KEY = process.env.REACT_APP_INVOICE_API_KEY;

const CLPFormat = new Intl.NumberFormat("es", {
  style: "currency",
  currency: "CLP",
});

export function formatAmount(number) {
  return `$${CLPFormat.format(number.toFixed(2)).replace("CLP", "").trim()}`;
}

// Emoji mapping by substring — order matters: longer/more specific first
const ITEM_EMOJI_MAP = [
  // Comida — específicos primero
  ["churrasco", "🌭"],
  ["as italiano", "🌭"],
  ["as dinamico", "🌭"],
  ["as dinámico", "🌭"],
  ["as completo", "🌭"],
  ["as chacarero", "🌭"],
  ["as gringo", "🌭"],
  ["as americano", "🌭"],
  ["as chemilico", "🌭"],
  ["luco", "🌭"],
  ["dinamica", "🌭"],
  ["dinámico", "🌭"],
  ["chacarero", "🌭"],
  ["chemilico", "🌭"],
  ["choripan", "🌭"],
  ["hot dog", "🌭"],
  ["completo", "🌭"],
  ["sandwich", "🌭"],
  ["sanwich", "🌭"],
  ["colonel sanders", "🍔"],
  ["avocado", "🍔"],
  ["smash", "🍔"],
  ["hamburguesa", "🍔"],
  ["burger", "🍔"],
  ["pizza", "🍕"],
  ["empanada", "🥟"],
  ["sushi", "🍣"],
  ["roll", "🍣"],
  ["maki", "🍣"],
  ["ceviche", "🦐"],
  ["marisco", "🦐"],
  ["camaron", "🦐"],
  ["papas", "🍟"],
  ["frita", "🍟"],
  ["ensalada", "🥗"],
  ["taco", "🌮"],
  ["general tso", "🍗"],
  ["pollo", "🍗"],
  ["alita", "🍗"],
  ["carne", "🥩"],
  ["asado", "🥩"],
  ["lomo", "🥩"],
  ["costilla", "🥩"],
  ["pasta", "🍝"],
  ["fideos", "🍝"],
  ["espagueti", "🍝"],
  ["queso", "🧀"],
  ["cheese", "🧀"],
  ["nachos", "🧀"],
  ["choclo", "🌽"],
  ["arroz", "🍚"],
  ["pan", "🍞"],
  ["marraqueta", "🍞"],
  ["tostada", "🍞"],
  ["mani", "🥜"],
  ["nueces", "🥜"],
  ["nuez", "🥜"],
  ["galleta", "🍪"],
  ["postre", "🍰"],
  ["torta", "🎂"],
  ["helado", "🍦"],
  ["brunch", "🥞"],
  ["tomate", "🍅"],
  ["fruta", "🍎"],
  ["manzana", "🍎"],
  ["platano", "🍌"],

  // Bebidas — marcas de cerveza
  ["tubinger", "🍺"],
  ["tübinger", "🍺"],
  ["austral", "🍺"],
  ["torobayo", "🍺"],
  ["kunstmann", "🍺"],
  ["kross", "🍺"],
  ["jester", "🍺"],
  ["granizo", "🍺"],
  ["tamango", "🍺"],
  ["cuello negro", "🍺"],
  ["bundor", "🍺"],
  ["soko", "🍺"],
  ["del puerto", "🍺"],
  ["becker", "🍺"],
  ["cristal", "🍺"],
  ["escudo", "🍺"],
  ["royal", "🍺"],
  ["heineken", "🍺"],
  ["corona", "🍺"],
  ["stella", "🍺"],
  ["shop", "🍺"],
  ["calafate", "🍺"],
  ["alameda", "🍺"],
  ["bier", "🍺"],
  ["cerveza", "🍺"],
  ["chela", "🍺"],
  ["birra", "🍺"],
  ["ipa", "🍺"],
  ["hazy", "🍺"],
  ["lager", "🍺"],
  ["stout", "🍺"],

  // Tragos
  ["piscola", "🥃"],
  ["pisco", "🥃"],
  ["tequila", "🥃"],
  ["whisky", "🥃"],
  ["ron", "🥃"],
  ["shot", "🥃"],
  ["jager", "🥃"],
  ["terremoto", "🍷"],
  ["pipeño", "🍷"],
  ["vino", "🍷"],
  ["tinto", "🍷"],
  ["blanco", "🍷"],
  ["aperol", "🍹"],
  ["spritz", "🍹"],
  ["ramazzotti", "🍹"],
  ["mojito", "🍹"],
  ["cocktail", "🍹"],
  ["trago", "🍹"],
  ["drink", "🍹"],

  // Bebidas sin alcohol
  ["cappuccino", "☕"],
  ["capuchino", "☕"],
  ["starbucks", "☕"],
  ["latte", "☕"],
  ["cafe", "☕"],
  ["café", "☕"],
  ["leche", "🥛"],
  ["jugo", "🧃"],
  ["limonada", "🧃"],
  ["bebida", "🥤"],
  ["coca", "🥤"],
  ["sprite", "🥤"],
  ["fanta", "🥤"],
  ["gaseosa", "🥤"],
  ["agua", "💧"],

  // Transporte
  ["taxi", "🚕"],
  ["uber", "🚗"],
  ["didi", "🚗"],
  ["cabify", "🚗"],
  ["bus", "🚌"],
  ["micro", "🚌"],
  ["metro", "🚇"],
  ["bencina", "⛽"],
  ["estacionamiento", "🅿️"],

  // Entretención
  ["cine", "🎬"],
  ["pelicula", "🎬"],
  ["entrada", "🎟️"],
  ["ticket", "🎟️"],
  ["karaoke", "🎤"],
  ["bowling", "🎳"],
  ["boliche", "🎳"],
  ["gym", "🏋️"],

  // Otros
  ["propina", "💰"],
  ["tip", "💰"],
  ["cover", "🎵"],
  ["hotel", "🏨"],
  ["libro", "📚"],
  ["super", "🛒"],
  ["mercado", "🛒"],
];

const DEFAULT_ITEM_EMOJI = "🍽️";

export function getItemEmoji(name) {
  const lower = name.toLowerCase().trim();
  if (!lower) return "";
  for (const [substring, emoji] of ITEM_EMOJI_MAP) {
    if (lower.includes(substring)) return emoji;
  }
  return DEFAULT_ITEM_EMOJI;
}

function newItem() {
  return {
    id: nanoid(),
    name: "",
    price: 0,
  };
}

// Curated list of cute OpenMoji animals — hexcodes from Unicode animal-mammal/bird/marine
const ANIMAL_AVATARS = [
  { hex: "1F436", name: "Perrito" },    // dog face
  { hex: "1F431", name: "Gatito" },     // cat face
  { hex: "1F43B", name: "Osito" },      // bear
  { hex: "1F428", name: "Koala" },      // koala
  { hex: "1F43C", name: "Panda" },      // panda
  { hex: "1F430", name: "Conejo" },     // rabbit
  { hex: "1F98A", name: "Zorro" },      // fox
  { hex: "1F981", name: "Leon" },       // lion
  { hex: "1F427", name: "Pinguino" },   // penguin
  { hex: "1F438", name: "Hamster" },    // hamster (actually this is hamster)
  { hex: "1F984", name: "Unicornio" },  // unicorn
  { hex: "1F42F", name: "Tigre" },      // tiger
  { hex: "1F435", name: "Monito" },     // monkey face
  { hex: "1F43A", name: "Lobo" },       // wolf
  { hex: "1F425", name: "Pollito" },    // baby chick
  { hex: "1F989", name: "Buho" },       // owl
];

export function getAnimalAvatar(hex) {
  return `https://cdn.jsdelivr.net/npm/openmoji@15.1.0/color/svg/${hex}.svg`;
}

// Pick a random animal that isn't already taken by existing friends
function pickAnimal(existingFriends) {
  const usedHexes = new Set(existingFriends.map((f) => f.animalHex));
  const available = ANIMAL_AVATARS.filter((a) => !usedHexes.has(a.hex));
  const pool = available.length > 0 ? available : ANIMAL_AVATARS;
  return pool[Math.floor(Math.random() * pool.length)].hex;
}

function newFriend(existingFriends = []) {
  return {
    id: nanoid(),
    name: "",
    animalHex: pickAnimal(existingFriends),
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "ADD_ITEM":
      return {
        ...state,
        splits: state.splits.concat(
          state.friends.map((friend) => ({
            friendId: friend.id,
            itemId: action.payload.id,
            amount: 0,
          }))
        ),
        items: [...state.items, action.payload],
      };
    case "ADD_FRIEND":
      return {
        ...state,
        splits: state.splits.concat(
          state.items.map((item) => ({
            friendId: action.payload.id,
            itemId: item.id,
            amount: 0,
          }))
        ),
        friends: [...state.friends, action.payload],
      };
    case "UPDATE_ITEM":
      return {
        ...state,
        items: state.items.map((item) => {
          if (item.id === action.payload.id) {
            return { ...item, ...action.payload };
          }
          return item;
        }),
      };
    case "UPDATE_FRIEND":
      return {
        ...state,
        friends: state.friends.map((friend) => {
          if (friend.id === action.payload.id) {
            return { ...friend, ...action.payload };
          }
          return friend;
        }),
      };
    case "UPDATE_SPLIT":
      return {
        ...state,
        splits: state.splits.map((split) => {
          if (
            split.itemId === action.payload.itemId &&
            split.friendId === action.payload.friendId
          ) {
            return { ...split, ...action.payload };
          }
          return split;
        }),
      };
    case "DELETE_ITEM":
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload.id),
        splits: state.splits.filter(
          (split) => split.itemId !== action.payload.id
        ),
      };
    case "DELETE_FRIEND":
      return {
        ...state,
        friends: state.friends.filter(
          (friend) => friend.id !== action.payload.id
        ),
        splits: state.splits.filter(
          (split) => split.friendId !== action.payload.id
        ),
      };
    case "BULK_ADD_ITEMS":
      const newItems = action.payload.map((item) => ({
        id: nanoid(),
        name: item.name,
        price: item.price,
      }));
      const newSplits = newItems.flatMap((item) =>
        state.friends.map((friend) => ({
          friendId: friend.id,
          itemId: item.id,
          amount: 0,
        }))
      );
      return {
        ...state,
        items: [...state.items, ...newItems],
        splits: [...state.splits, ...newSplits],
      };
    default:
      return state;
  }
}

function getInitialState() {
  const expense = get();

  if (expense) {
    return expense;
  }

  return {
    items: [newItem()],
    friends: [],
    splits: [],
  };
}

export default function Expense({ onBack }) {
  const [state, dispatch] = useReducer(reducer, null, getInitialState);

  useEffect(() => {
    set(state);
  }, [state]);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!API_KEY) {
      setUploadError(
        "API key no configurada. Agrega REACT_APP_INVOICE_API_KEY a tu archivo .env"
      );
      return;
    }

    setUploading(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API_URL}/extract`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error("Límite de peticiones excedido. Espera un minuto.");
        }
        if (response.status === 401) {
          throw new Error("API key inválida.");
        }
        throw new Error("Error al procesar la imagen.");
      }

      const data = await response.json();
      if (data.items && data.items.length > 0) {
        dispatch({ type: "BULK_ADD_ITEMS", payload: data.items });
      } else {
        setUploadError("No se encontraron items en la imagen.");
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const { items, friends, splits } = state;

  const getContribution = (itemId, friendId) => {
    return (
      splits.find(
        (split) => split.itemId === itemId && split.friendId === friendId
      )?.amount || ""
    );
  };

  const getFriendTotal = (friendId) => {
    let total = 0;

    items.forEach((item) => {
      const totalDistribution = splits
        .filter((split) => split.itemId === item.id)
        .reduce((total, { amount }) => total + amount, 0);
      const cuota =
        totalDistribution === 0 ? 0 : item.price / totalDistribution;
      total += cuota * getContribution(item.id, friendId);
    });

    return total;
  };

  const getTotalConsumption = () => {
    let totalConsumption = 0;
    items.forEach((item) => {
      totalConsumption += item.price;
    });
    return totalConsumption;
  };

  const getTip = (mount) => {
    const tipValue = 0.1;
    return mount * tipValue;
  };

  return (
    <div className="expense-page">
      <div className="expense-header">
        <button className="btn-back" onClick={onBack} title="Volver">
          🍃
        </button>
        <h2 className="expense-title">Tu Cuenta</h2>
      </div>

      <div className="ledger">
        <div className="ledger-scroll">
          <table className="ledger-table">
            <thead>
              <tr>
                <th className="col-emoji"></th>
                <th className="col-item">Item</th>
                <th className="col-price">Precio</th>
                {friends.map(({ id, name, animalHex }, index) => (
                  <th key={id} className="col-friend">
                    <div className="friend-header">
                      <div
                        className={`friend-avatar friend-avatar-${index % 8}`}
                      >
                        {animalHex ? (
                          <img
                            src={getAnimalAvatar(animalHex)}
                            alt="avatar"
                            className="friend-avatar-img"
                          />
                        ) : (
                          "?"
                        )}
                      </div>
                      <div className="friend-name-group">
                        <input
                          type="text"
                          className="input input-friend"
                          placeholder="Nombre"
                          value={name}
                          onChange={(event) => {
                            dispatch({
                              type: "UPDATE_FRIEND",
                              payload: { id, name: event.target.value },
                            });
                          }}
                        />
                        <button
                          className="btn-icon"
                          onClick={() => {
                            dispatch({
                              type: "DELETE_FRIEND",
                              payload: { id },
                            });
                          }}
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
                <th>
                  <button
                    className="btn-add-friend"
                    onClick={() =>
                      dispatch({ type: "ADD_FRIEND", payload: newFriend(friends) })
                    }
                  >
                    <div className="btn-add-friend-icon">+</div>
                    <span>Invitar</span>
                  </button>
                </th>
              </tr>
            </thead>
            {(() => {
              const itemsWithPrice = items.filter((i) => i.price > 0);
              const friendColumnEmpty = {};
              friends.forEach((f) => {
                friendColumnEmpty[f.id] =
                  itemsWithPrice.length > 0 &&
                  itemsWithPrice.every((i) => {
                    const c = getContribution(i.id, f.id);
                    return !c || c === 0;
                  });
              });
              return (
            <tbody>
              {items.map(({ id, price, name }) => {
                const hasName = name.trim().length > 0;
                const hasPrice = price > 0;
                const emoji = getItemEmoji(name);
                return (
                <tr key={id}>
                  <td className="col-emoji">{emoji}</td>
                  <td className="col-item">
                    <input
                      type="text"
                      className={`input input-name${!hasName && hasPrice ? " input-empty-warn" : ""}`}
                      placeholder="Nombre de item"
                      value={name}
                      onChange={(event) => {
                        dispatch({
                          type: "UPDATE_ITEM",
                          payload: { id, name: event.target.value },
                        });
                      }}
                    />
                  </td>
                  <td className="col-price">
                    <input
                      type="number"
                      className={`input input-number${!hasPrice && hasName ? " input-empty-warn" : ""}`}
                      placeholder="$"
                      value={price ? price : ""}
                      onChange={(event) => {
                        dispatch({
                          type: "UPDATE_ITEM",
                          payload: {
                            id,
                            name,
                            price: parseInt(
                              event.target.value ? event.target.value : 0
                            ),
                          },
                        });
                      }}
                    />
                  </td>
                  {friends.map((friend) => {
                    const contribution = getContribution(id, friend.id);
                    const isEmpty = !contribution || contribution === 0;
                    const rowHasAnyContribution = friends.some(
                      (f) => {
                        const c = getContribution(id, f.id);
                        return c && c > 0;
                      }
                    );
                    const warn = isEmpty && price > 0 && (!rowHasAnyContribution || friendColumnEmpty[friend.id]);
                    return (
                    <td key={friend.id} className="col-friend">
                      <input
                        type="number"
                        className={`input input-number${warn ? " input-empty-warn" : ""}`}
                        placeholder="0"
                        value={contribution}
                        min={0}
                        onChange={(event) => {
                          dispatch({
                            type: "UPDATE_SPLIT",
                            payload: {
                              itemId: id,
                              friendId: friend.id,
                              amount: parseInt(
                                event.target.value ? event.target.value : 0
                              ),
                            },
                          });
                        }}
                      />
                    </td>
                    );
                  })}
                  <td className="col-actions">
                    <button
                      className="btn-icon"
                      onClick={() => {
                        dispatch({ type: "DELETE_ITEM", payload: { id } });
                      }}
                      title="Borrar item"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
              );
            })()}
            <tfoot>
              <tr>
                <td colSpan={2}>Total Consumo</td>
                <td>{formatAmount(getTotalConsumption())}</td>
                {friends.map((friend) => (
                  <td key={friend.id}>
                    {formatAmount(getFriendTotal(friend.id))}
                  </td>
                ))}
                <td></td>
              </tr>
              <tr>
                <td colSpan={2}>Propina (10%)</td>
                <td>{formatAmount(getTip(getTotalConsumption()))}</td>
                {friends.map((friend) => (
                  <td key={friend.id}>
                    {formatAmount(getTip(getFriendTotal(friend.id)))}
                  </td>
                ))}
                <td></td>
              </tr>
              <tr className="row-total">
                <td colSpan={2}>
                  <span className="total-label">
                    <span className="bell-icon">🔔</span>
                    Total a pagar
                  </span>
                </td>
                <td>
                  {formatAmount(
                    getTotalConsumption() + getTip(getTotalConsumption())
                  )}
                </td>
                {friends.map((friend) => (
                  <td key={friend.id}>
                    {formatAmount(
                      getFriendTotal(friend.id) +
                        getTip(getFriendTotal(friend.id))
                    )}
                  </td>
                ))}
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="action-bar">
        <button
          className="btn btn-primary"
          onClick={() => dispatch({ type: "ADD_ITEM", payload: newItem() })}
        >
          🍔 Agregar item
        </button>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          ref={fileInputRef}
          style={{ display: "none" }}
          id="invoice-upload"
        />
        <label
          htmlFor="invoice-upload"
          className="upload-label"
          data-uploading={uploading}
        >
          {uploading ? "⏳ Procesando..." : "📷 Subir boleta"}
        </label>
      </div>

      {uploadError && <div className="error-msg">{uploadError}</div>}
    </div>
  );
}
